import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { Plus, X, Truck, AlertTriangle, Tags, Trash2, CalendarClock, PackageX, Upload, Download, Camera, ImageOff } from "lucide-react";
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useAdjustProductStock,
  useUploadProductImage,
  useDeleteProductImage,
  useSuppliers,
  useCreateSupplier,
  useUpdateSupplier,
  useSupplierBatches,
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useProductBatches,
  useCreateBatch,
  useConsumeBatch,
  useExpiringBatches,
  useImportProducts,
  downloadProductImportTemplate,
  useBranches,
  PRODUCT_UNIT_LABEL,
  ProductUnit,
  ProductRecord,
  CategoryRecord,
  BatchRecord,
  SupplierRecord,
  ImportProductsResult,
} from "@/api/inventory";
import { getApiErrorMessage } from "@/api/client";
import { ProductImage } from "@/components/common/ProductImage";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });
const UNIT_OPTIONS = Object.keys(PRODUCT_UNIT_LABEL) as ProductUnit[];

type ConsumeBatchReason = "VENCIDO" | "DANADO" | "DEVUELTO_PROVEEDOR" | "OTRO";
const CONSUME_REASON_LABEL: Record<ConsumeBatchReason, string> = {
  VENCIDO: "Vencido (se bota)",
  DANADO: "Dañado (se bota)",
  DEVUELTO_PROVEEDOR: "Devuelto al proveedor (nota crédito)",
  OTRO: "Otro motivo",
};

/** Pregunta el motivo de la baja con un prompt numerado (1-4) — mismo patrón liviano de window.prompt/confirm ya usado en el resto de la página, sin agregar un modal nuevo solo para esto. */
function promptConsumeReason(): ConsumeBatchReason | null {
  const options = Object.keys(CONSUME_REASON_LABEL) as ConsumeBatchReason[];
  const menu = options.map((key, i) => `${i + 1}. ${CONSUME_REASON_LABEL[key]}`).join("\n");
  const input = window.prompt(`Motivo de la baja:\n${menu}\n\nEscribe el número (1-${options.length}):`, "1");
  if (!input) return null;
  const index = Number(input.trim()) - 1;
  return options[index] ?? null;
}

/**
 * Productos de la tienda (primer vertical comercial — víveres/abarrotes).
 * Incluye la gestión de proveedores en un panel lateral, ya que ambos
 * catálogos se usan juntos al registrar una compra (Compras).
 */
export default function ProductsPage() {
  const [search, setSearch] = useState("");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ProductRecord | null>(null);
  const [adjusting, setAdjusting] = useState<ProductRecord | null>(null);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showExpiring, setShowExpiring] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [batchesFor, setBatchesFor] = useState<ProductRecord | null>(null);

  const { data, isLoading } = useProducts({ search: search || undefined, lowStock: lowStockOnly || undefined, categoryId: categoryFilter || undefined, pageSize: 100 });
  const products = data?.data ?? [];
  const { data: categories } = useCategories(true);
  const { data: expiringBatches } = useExpiringBatches(30);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Productos</h1>
          <p className="text-sm text-gray-500">Catálogo de productos, precios e inventario de la tienda.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary relative" onClick={() => setShowExpiring(true)}>
            <CalendarClock size={16} className="mr-1.5" /> Vencimientos
            {expiringBatches && expiringBatches.length > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-semibold text-white">
                {expiringBatches.length}
              </span>
            )}
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowCategories(true)}>
            <Tags size={16} className="mr-1.5" /> Categorías
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowSuppliers(true)}>
            <Truck size={16} className="mr-1.5" /> Proveedores
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={16} className="mr-1.5" /> Importar
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo producto
          </button>
        </div>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input className="input-field min-w-[220px] flex-1" placeholder="Buscar por nombre, SKU o código de barras..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input-field w-auto" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categories?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-brand-600" checked={lowStockOnly} onChange={(e) => setLowStockOnly(e.target.checked)} />
          Solo stock bajo
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Foto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">SKU</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Categoría</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Costo</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Precio</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Stock</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && products.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  No hay productos. Crea el primero con "Nuevo producto".
                </td>
              </tr>
            )}
            {products.map((p) => {
              const low = Number(p.stockQty) <= Number(p.minStock);
              return (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <ProductImage productId={p.id} hasImage={p.hasImage} alt={p.name} className="h-10 w-10" iconSize={16} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.sku}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {p.name}
                    {!p.isActive && <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">Inactivo</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{p.category?.name ?? <span className="text-gray-300">—</span>}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{currency.format(Number(p.costPrice))}</td>
                  <td className="px-4 py-3 text-right text-gray-900">
                    {currency.format(Number(p.salePrice))}
                    {p.marginPercent !== null && (
                      <div className={`text-xs font-normal ${p.marginPercent < 0 ? "text-red-600" : "text-gray-400"}`}>{p.marginPercent}% margen</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${low ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
                      {low && <AlertTriangle size={12} />}
                      {p.stockQty} {PRODUCT_UNIT_LABEL[p.unit]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" className="mr-3 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setEditing(p)}>
                      Editar
                    </button>
                    <button type="button" className="mr-3 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setAdjusting(p)}>
                      Ajustar stock
                    </button>
                    <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => setBatchesFor(p)}>
                      Lotes
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(showCreate || editing) && <ProductFormModal product={editing} onClose={() => { setShowCreate(false); setEditing(null); }} />}
      {adjusting && <AdjustStockModal product={adjusting} onClose={() => setAdjusting(null)} />}
      {showSuppliers && <SuppliersModal onClose={() => setShowSuppliers(false)} />}
      {showCategories && <CategoriesModal onClose={() => setShowCategories(false)} />}
      {batchesFor && <BatchesModal product={batchesFor} onClose={() => setBatchesFor(null)} />}
      {showExpiring && <ExpiringBatchesModal onClose={() => setShowExpiring(false)} />}
      {showImport && <ImportModal onClose={() => setShowImport(false)} />}
    </div>
  );
}

function ProductFormModal({ product, onClose }: { product: ProductRecord | null; onClose: () => void }) {
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const uploadImage = useUploadProductImage();
  const deleteImage = useDeleteProductImage();
  const { data: categories } = useCategories(true);
  const { data: suppliers } = useSuppliers(true);
  const isEdit = !!product;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !product) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Formato no permitido. Use PNG, JPEG o WEBP.");
      return;
    }
    try {
      await uploadImage.mutateAsync({ id: product.id, file });
      toast.success("Foto del producto actualizada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleRemoveImage = async () => {
    if (!product) return;
    try {
      await deleteImage.mutateAsync(product.id);
      toast.success("Foto eliminada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const [categoryId, setCategoryId] = useState(product?.categoryId ?? "");
  const [subcategoryId, setSubcategoryId] = useState(product?.subcategoryId ?? "");
  const [sku, setSku] = useState(product?.sku ?? "");
  const [barcode, setBarcode] = useState(product?.barcode ?? "");
  const [name, setName] = useState(product?.name ?? "");
  const [brand, setBrand] = useState(product?.brand ?? "");
  const [presentation, setPresentation] = useState(product?.presentation ?? "");
  const [unit, setUnit] = useState<ProductUnit>(product?.unit ?? "UNIDAD");
  const [costPrice, setCostPrice] = useState(product?.costPrice ?? "0");
  const [salePrice, setSalePrice] = useState(product?.salePrice ?? "0");
  const [wholesalePrice, setWholesalePrice] = useState(product?.wholesalePrice ?? "");
  const [distributorPrice, setDistributorPrice] = useState(product?.distributorPrice ?? "");
  const [taxRate, setTaxRate] = useState(product?.taxRate ?? "0");
  const [minStock, setMinStock] = useState(product?.minStock ?? "0");
  const [maxStock, setMaxStock] = useState(product?.maxStock ?? "");
  const [maxDiscountPercent, setMaxDiscountPercent] = useState(product?.maxDiscountPercent ?? "");
  const [primarySupplierId, setPrimarySupplierId] = useState(product?.primarySupplierId ?? "");
  const [initialStock, setInitialStock] = useState("0");
  const [warrantyMonths, setWarrantyMonths] = useState(product?.warrantyMonths != null ? String(product.warrantyMonths) : "");

  const subcategoryOptions = categories?.filter((c) => c.parentCategoryId === (categoryId || null)) ?? [];
  const liveMarginPercent = Number(salePrice) > 0 ? Math.round(((Number(salePrice) - Number(costPrice)) / Number(salePrice)) * 1000) / 10 : null;

  const isValid = sku.trim().length > 0 && name.trim().length >= 2;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("El SKU y el nombre son obligatorios.");
      return;
    }
    try {
      if (isEdit && product) {
        await updateProduct.mutateAsync({
          id: product.id,
          categoryId: categoryId || null,
          subcategoryId: subcategoryId || null,
          sku,
          barcode: barcode || undefined,
          name,
          brand: brand || null,
          presentation: presentation || null,
          unit,
          costPrice,
          salePrice,
          wholesalePrice: wholesalePrice === "" ? null : wholesalePrice,
          distributorPrice: distributorPrice === "" ? null : distributorPrice,
          taxRate,
          minStock,
          maxStock: maxStock === "" ? null : maxStock,
          maxDiscountPercent: maxDiscountPercent === "" ? null : maxDiscountPercent,
          primarySupplierId: primarySupplierId || null,
          warrantyMonths: warrantyMonths === "" ? null : warrantyMonths,
        });
        toast.success("Producto actualizado.");
      } else {
        await createProduct.mutateAsync({
          categoryId: categoryId || undefined,
          subcategoryId: subcategoryId || undefined,
          sku,
          barcode: barcode || undefined,
          name,
          brand: brand || undefined,
          presentation: presentation || undefined,
          unit,
          costPrice,
          salePrice,
          wholesalePrice: wholesalePrice === "" ? undefined : wholesalePrice,
          distributorPrice: distributorPrice === "" ? undefined : distributorPrice,
          taxRate,
          minStock,
          maxStock: maxStock === "" ? undefined : maxStock,
          maxDiscountPercent: maxDiscountPercent === "" ? undefined : maxDiscountPercent,
          primarySupplierId: primarySupplierId || undefined,
          initialStock,
          warrantyMonths: warrantyMonths === "" ? undefined : warrantyMonths,
        });
        toast.success("Producto creado.");
      }
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{isEdit ? "Editar producto" : "Nuevo producto"}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[75vh] space-y-3 overflow-y-auto p-4">
          {isEdit && product && (
            <div className="flex items-center gap-3 rounded-md border border-dashed border-gray-200 p-3">
              <ProductImage productId={product.id} hasImage={product.hasImage} alt={product.name} className="h-16 w-16" iconSize={22} />
              <div className="flex flex-1 flex-col gap-1.5">
                <p className="text-xs text-gray-500">Foto del producto (se muestra en el Validador de precios y en el catálogo). PNG, JPEG o WEBP.</p>
                <div className="flex gap-2">
                  <button type="button" className="btn-secondary px-2.5 py-1 text-xs" disabled={uploadImage.isPending} onClick={() => fileInputRef.current?.click()}>
                    <Camera size={13} className="mr-1" /> {uploadImage.isPending ? "Subiendo..." : product.hasImage ? "Cambiar foto" : "Subir foto"}
                  </button>
                  {product.hasImage && (
                    <button type="button" className="btn-secondary px-2.5 py-1 text-xs text-red-600" disabled={deleteImage.isPending} onClick={handleRemoveImage}>
                      <ImageOff size={13} className="mr-1" /> Quitar
                    </button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleImageSelected} />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">SKU</label>
              <input className="input-field mt-1" value={sku} onChange={(e) => setSku(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Código de barras</label>
              <input className="input-field mt-1" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre</label>
            <input className="input-field mt-1" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Marca</label>
              <input className="input-field mt-1" placeholder="Ej. Alpina" value={brand} onChange={(e) => setBrand(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Presentación</label>
              <input className="input-field mt-1" placeholder="Ej. Bolsa 900 g" value={presentation} onChange={(e) => setPresentation(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Categoría</label>
              <select
                className="input-field mt-1"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setSubcategoryId("");
                }}
              >
                <option value="">Sin categoría</option>
                {categories?.filter((c) => !c.parentCategoryId).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subcategoría</label>
              <select
                className="input-field mt-1"
                value={subcategoryId}
                onChange={(e) => setSubcategoryId(e.target.value)}
                disabled={subcategoryOptions.length === 0}
              >
                <option value="">Sin subcategoría</option>
                {subcategoryOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {categoryId && subcategoryOptions.length === 0 && (
                <p className="mt-1 text-xs text-gray-400">Esta categoría no tiene subcategorías (se crean desde "Categorías").</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Unidad</label>
              <select className="input-field mt-1" value={unit} onChange={(e) => setUnit(e.target.value as ProductUnit)}>
                {UNIT_OPTIONS.map((u) => (
                  <option key={u} value={u}>
                    {PRODUCT_UNIT_LABEL[u]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">IVA (%)</label>
              <input className="input-field mt-1" type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Costo</label>
              <input className="input-field mt-1" type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Precio de venta</label>
              <input className="input-field mt-1" type="number" value={salePrice} onChange={(e) => setSalePrice(e.target.value)} />
            </div>
          </div>
          {liveMarginPercent !== null && (
            <p className={`text-xs ${liveMarginPercent < 0 ? "text-red-600" : "text-gray-500"}`}>
              Margen sobre el precio de venta: <span className="font-medium">{liveMarginPercent}%</span>
              {liveMarginPercent < 0 && " — el precio de venta queda por debajo del costo"}
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Precio mayorista</label>
              <input className="input-field mt-1" type="number" placeholder="—" value={wholesalePrice} onChange={(e) => setWholesalePrice(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Precio distribuidor</label>
              <input className="input-field mt-1" type="number" placeholder="—" value={distributorPrice} onChange={(e) => setDistributorPrice(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Stock mínimo (alerta)</label>
              <input className="input-field mt-1" type="number" value={minStock} onChange={(e) => setMinStock(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Stock máximo</label>
              <input className="input-field mt-1" type="number" placeholder="—" value={maxStock} onChange={(e) => setMaxStock(e.target.value)} />
            </div>
          </div>
          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Stock inicial</label>
              <input className="input-field mt-1" type="number" value={initialStock} onChange={(e) => setInitialStock(e.target.value)} />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700" title="El POS rechazará descuentos mayores a este % en este producto">
                Descuento máx. (%)
              </label>
              <input className="input-field mt-1" type="number" min="0" max="100" placeholder="Sin límite" value={maxDiscountPercent} onChange={(e) => setMaxDiscountPercent(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Proveedor principal</label>
              <select className="input-field mt-1" value={primarySupplierId} onChange={(e) => setPrimarySupplierId(e.target.value)}>
                <option value="">Sin definir</option>
                {suppliers?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Garantía (meses)</label>
            <input
              className="input-field mt-1"
              type="number"
              min="0"
              placeholder="Sin garantía"
              value={warrantyMonths}
              onChange={(e) => setWarrantyMonths(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-400">Deje vacío si el producto no tiene garantía. Se aplica a las ventas nuevas, no cambia las ya hechas.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={!isValid || createProduct.isPending || updateProduct.isPending} onClick={handleSubmit}>
            {createProduct.isPending || updateProduct.isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdjustStockModal({ product, onClose }: { product: ProductRecord; onClose: () => void }) {
  const adjustStock = useAdjustProductStock();
  const { data: branches } = useBranches(true);
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [branchId, setBranchId] = useState("");

  const handleSubmit = async () => {
    const qty = Number(quantity);
    if (!qty) {
      toast.error("Ingrese una cantidad distinta de cero (positiva para agregar, negativa para restar).");
      return;
    }
    try {
      await adjustStock.mutateAsync({ id: product.id, branchId: branchId || undefined, quantity: qty, notes: notes || undefined });
      toast.success("Ajuste de inventario registrado.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Ajustar stock — {product.name}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-xs text-gray-500">Stock total (todas las sucursales): {product.stockQty} {PRODUCT_UNIT_LABEL[product.unit]}</p>
          {branches && branches.length > 1 && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Sucursal</label>
              <select className="input-field mt-1" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Principal (predeterminada)</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700">Cantidad (+ agrega, - resta)</label>
            <input className="input-field mt-1" type="number" value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Ej: 10 o -3" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Motivo</label>
            <input className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Conteo físico, merma, etc." />
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={adjustStock.isPending} onClick={handleSubmit}>
            {adjustStock.isPending ? "Guardando..." : "Registrar ajuste"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoriesModal({ onClose }: { onClose: () => void }) {
  const { data: categories, isLoading } = useCategories();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const [name, setName] = useState("");
  const [parentCategoryId, setParentCategoryId] = useState("");

  const topLevelCategories = categories?.filter((c) => c.isActive && !c.parentCategoryId) ?? [];

  const handleCreate = async () => {
    if (name.trim().length < 1) {
      toast.error("El nombre de la categoría es obligatorio.");
      return;
    }
    try {
      await createCategory.mutateAsync({ name, parentCategoryId: parentCategoryId || undefined });
      toast.success(parentCategoryId ? "Subcategoría creada." : "Categoría creada.");
      setName("");
      setParentCategoryId("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleRename = async (category: CategoryRecord) => {
    const newName = window.prompt("Nuevo nombre de la categoría", category.name);
    if (!newName || newName === category.name) return;
    try {
      await updateCategory.mutateAsync({ id: category.id, name: newName });
      toast.success("Categoría actualizada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDeactivate = async (category: CategoryRecord) => {
    if (!window.confirm(`¿Desactivar "${category.name}"? Los productos que la usan quedarán sin categoría.`)) return;
    try {
      await deleteCategory.mutateAsync(category.id);
      toast.success("Categoría desactivada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Categorías de productos</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {categories?.filter((c) => c.isActive && !c.parentCategoryId).map((c) => (
            <div key={c.id}>
              <div className="flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-gray-50">
                <span className="font-medium text-gray-700">{c.name}</span>
                <div className="flex items-center gap-3">
                  <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleRename(c)}>
                    Renombrar
                  </button>
                  <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => handleDeactivate(c)} title="Desactivar">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {categories.filter((sc) => sc.isActive && sc.parentCategoryId === c.id).map((sc) => (
                <div key={sc.id} className="ml-4 flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-gray-50">
                  <span className="text-gray-500">↳ {sc.name}</span>
                  <div className="flex items-center gap-3">
                    <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleRename(sc)}>
                      Renombrar
                    </button>
                    <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => handleDeactivate(sc)} title="Desactivar">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
          {categories?.filter((c) => c.isActive).length === 0 && <p className="text-sm text-gray-400">Ninguna categoría todavía.</p>}
        </div>
        <div className="space-y-2 border-t border-gray-100 p-4">
          <div className="grid grid-cols-3 gap-2">
            <input className="input-field col-span-2" placeholder="Nombre de la nueva categoría o subcategoría" value={name} onChange={(e) => setName(e.target.value)} />
            <button type="button" className="btn-primary col-span-1" disabled={createCategory.isPending} onClick={handleCreate}>
              {createCategory.isPending ? "Agregando..." : "Agregar"}
            </button>
          </div>
          <select className="input-field" value={parentCategoryId} onChange={(e) => setParentCategoryId(e.target.value)}>
            <option value="">Categoría de nivel superior (sin padre)</option>
            {topLevelCategories.map((c) => (
              <option key={c.id} value={c.id}>
                Subcategoría de: {c.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function BatchesModal({ product, onClose }: { product: ProductRecord; onClose: () => void }) {
  const { data: batches, isLoading } = useProductBatches(product.id);
  const { data: suppliers } = useSuppliers(true);
  const createBatch = useCreateBatch(product.id);
  const consumeBatch = useConsumeBatch();
  const [batchNumber, setBatchNumber] = useState("");
  const [quantity, setQuantity] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierLotRef, setSupplierLotRef] = useState("");

  const handleCreate = async () => {
    const qty = Number(quantity);
    if (!qty || qty <= 0) {
      toast.error("Ingrese una cantidad mayor a cero.");
      return;
    }
    try {
      await createBatch.mutateAsync({
        batchNumber: batchNumber || undefined,
        quantity: qty,
        expirationDate: expirationDate || undefined,
        supplierId: supplierId || undefined,
        supplierLotRef: supplierLotRef || undefined,
      });
      toast.success("Lote registrado.");
      setBatchNumber("");
      setQuantity("");
      setExpirationDate("");
      setSupplierId("");
      setSupplierLotRef("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleConsume = async (batch: BatchRecord) => {
    const input = window.prompt(`¿Cuántas unidades del lote${batch.batchNumber ? ` "${batch.batchNumber}"` : ""} quieres dar de baja? (disponible: ${batch.quantity})`, batch.quantity);
    if (!input) return;
    const qty = Number(input);
    if (!qty || qty <= 0) {
      toast.error("Cantidad inválida.");
      return;
    }
    const reason = promptConsumeReason();
    if (!reason) return;
    try {
      await consumeBatch.mutateAsync({ id: batch.id, quantity: qty, reason });
      toast.success("Baja registrada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Lotes — {product.name}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {batches?.map((b) => {
            const expired = b.expirationDate && b.expirationDate <= today;
            const soon = b.expirationDate && !expired && b.expirationDate <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
            return (
              <div key={b.id} className="flex items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-gray-50">
                <div>
                  <p className="text-gray-700">
                    {b.batchNumber ?? "Sin número"} · {b.quantity} {PRODUCT_UNIT_LABEL[product.unit]}
                  </p>
                  {b.expirationDate && (
                    <p className={`text-xs ${expired ? "text-red-600" : soon ? "text-amber-600" : "text-gray-400"}`}>
                      Vence: {b.expirationDate} {expired ? "(vencido)" : soon ? "(próximo a vencer)" : ""}
                    </p>
                  )}
                  {(b.supplier || b.supplierLotRef) && (
                    <p className="text-xs text-gray-400">
                      Proveedor: {b.supplier?.name ?? "—"} {b.supplierLotRef ? `· ref. proveedor: ${b.supplierLotRef}` : ""}
                    </p>
                  )}
                </div>
                {b.isActive && Number(b.quantity) > 0 && (
                  <button type="button" className="text-xs font-medium text-red-600 hover:text-red-800" onClick={() => handleConsume(b)}>
                    Dar de baja
                  </button>
                )}
              </div>
            );
          })}
          {batches?.length === 0 && <p className="text-sm text-gray-400">Este producto no tiene lotes registrados.</p>}
        </div>
        <div className="space-y-2 border-t border-gray-100 p-4">
          <div className="grid grid-cols-3 gap-2">
            <input className="input-field" placeholder="N.° de lote" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} />
            <input className="input-field" type="number" placeholder="Cantidad" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <input className="input-field" type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <select className="input-field" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Proveedor (trazabilidad)</option>
              {suppliers?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            <input className="input-field" placeholder="Ref. de lote del proveedor" value={supplierLotRef} onChange={(e) => setSupplierLotRef(e.target.value)} />
            <button type="button" className="btn-primary" disabled={createBatch.isPending} onClick={handleCreate}>
              {createBatch.isPending ? "Agregando..." : "Agregar lote"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpiringBatchesModal({ onClose }: { onClose: () => void }) {
  const { data: batches, isLoading } = useExpiringBatches(30);
  const consumeBatch = useConsumeBatch();
  const today = new Date().toISOString().slice(0, 10);

  const handleConsume = async (batch: BatchRecord) => {
    const input = window.prompt(`¿Cuántas unidades dar de baja? (disponible: ${batch.quantity})`, batch.quantity);
    if (!input) return;
    const qty = Number(input);
    if (!qty || qty <= 0) {
      toast.error("Cantidad inválida.");
      return;
    }
    const reason = promptConsumeReason();
    if (!reason) return;
    try {
      await consumeBatch.mutateAsync({ id: batch.id, quantity: qty, reason });
      toast.success("Baja registrada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Lotes próximos a vencer (30 días)</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {batches?.map((b) => {
            const expired = !!b.expirationDate && b.expirationDate <= today;
            return (
              <div key={b.id} className="flex items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-gray-50">
                <div>
                  <p className="text-gray-700">
                    {b.product?.name} <span className="text-gray-400">({b.product?.sku})</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {b.batchNumber ?? "Sin número"} · {b.quantity} {b.product ? PRODUCT_UNIT_LABEL[b.product.unit] : ""}
                    {b.supplier ? ` · ${b.supplier.name}` : ""}
                  </p>
                  <p className={`text-xs font-medium ${expired ? "text-red-600" : "text-amber-600"}`}>Vence: {b.expirationDate} {expired ? "(vencido)" : ""}</p>
                </div>
                <button type="button" className="flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-800" onClick={() => handleConsume(b)}>
                  <PackageX size={14} /> Dar de baja
                </button>
              </div>
            );
          })}
          {batches?.length === 0 && <p className="text-sm text-gray-400">No hay lotes próximos a vencer en los siguientes 30 días.</p>}
        </div>
      </div>
    </div>
  );
}

function ImportModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportProductsResult | null>(null);
  const importProducts = useImportProducts();

  const handleImport = async () => {
    if (!file) {
      toast.error("Seleccione un archivo .xlsx primero.");
      return;
    }
    try {
      const data = await importProducts.mutateAsync(file);
      setResult(data);
      if (data.errors === 0) {
        toast.success(`${data.created} creados, ${data.updated} actualizados.`);
      } else {
        toast.error(`${data.errors} fila(s) con error. Revise el detalle abajo.`);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Carga masiva de productos</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <p className="text-sm text-gray-500">
            Suba un archivo .xlsx con sus productos. Si el SKU ya existe se actualiza (nombre, precios, categoría — nunca el stock); si es nuevo, se crea con el stock inicial indicado.
          </p>
          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:text-primary-900"
            onClick={() => void downloadProductImportTemplate()}
          >
            <Download size={16} /> Descargar plantilla de ejemplo
          </button>

          <div className="rounded-md border border-dashed border-gray-300 p-4">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setResult(null);
              }}
              className="block w-full text-sm text-gray-600"
            />
            {file && <p className="mt-2 text-xs text-gray-500">Archivo seleccionado: {file.name}</p>}
          </div>

          <button type="button" className="btn-primary w-full" disabled={!file || importProducts.isPending} onClick={handleImport}>
            <Upload size={16} className="mr-1.5" /> {importProducts.isPending ? "Importando..." : "Importar productos"}
          </button>

          {result && (
            <div className="space-y-2">
              <div className="flex gap-4 rounded-md bg-gray-50 p-3 text-sm">
                <span className="text-gray-700">Total: {result.totalRows}</span>
                <span className="text-green-700">Creados: {result.created}</span>
                <span className="text-blue-700">Actualizados: {result.updated}</span>
                <span className="text-red-700">Errores: {result.errors}</span>
              </div>
              <div className="max-h-64 overflow-y-auto rounded-md border border-gray-100">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Fila</th>
                      <th className="px-2 py-1.5 text-left">SKU</th>
                      <th className="px-2 py-1.5 text-left">Estado</th>
                      <th className="px-2 py-1.5 text-left">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {result.rows.map((r) => (
                      <tr key={r.row}>
                        <td className="px-2 py-1.5 text-gray-500">{r.row}</td>
                        <td className="px-2 py-1.5 text-gray-700">{r.sku ?? "—"}</td>
                        <td className="px-2 py-1.5">
                          <span
                            className={`rounded-full px-2 py-0.5 font-medium ${
                              r.status === "created"
                                ? "bg-green-100 text-green-700"
                                : r.status === "updated"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-red-100 text-red-700"
                            }`}
                          >
                            {r.status === "created" ? "Creado" : r.status === "updated" ? "Actualizado" : "Error"}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">{r.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {result.rows.length === 0 && <p className="p-3 text-sm text-gray-400">El archivo no tiene filas con datos.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SuppliersModal({ onClose }: { onClose: () => void }) {
  const [showInactive, setShowInactive] = useState(false);
  const { data: suppliers, isLoading } = useSuppliers(showInactive ? undefined : true);
  const createSupplier = useCreateSupplier();
  const updateSupplier = useUpdateSupplier();
  const [name, setName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [phone, setPhone] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState({ name: "", taxId: "", phone: "", email: "", address: "" });
  const [traceFor, setTraceFor] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = async () => {
    if (name.trim().length < 2) {
      toast.error("El nombre del proveedor es obligatorio.");
      return;
    }
    try {
      await createSupplier.mutateAsync({ name, taxId: taxId || undefined, phone: phone || undefined });
      toast.success("Proveedor creado.");
      setName("");
      setTaxId("");
      setPhone("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const startEdit = (s: SupplierRecord) => {
    setEditingId(s.id);
    setEditDraft({ name: s.name, taxId: s.taxId ?? "", phone: s.phone ?? "", email: s.email ?? "", address: s.address ?? "" });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (editDraft.name.trim().length < 2) {
      toast.error("El nombre del proveedor es obligatorio.");
      return;
    }
    try {
      await updateSupplier.mutateAsync({
        id: editingId,
        name: editDraft.name,
        taxId: editDraft.taxId || undefined,
        phone: editDraft.phone || undefined,
        email: editDraft.email || undefined,
        address: editDraft.address || undefined,
      });
      toast.success("Proveedor actualizado.");
      setEditingId(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const toggleActive = async (s: SupplierRecord) => {
    const verb = s.isActive ? "desactivar" : "reactivar";
    if (!window.confirm(`¿${verb.charAt(0).toUpperCase() + verb.slice(1)} a "${s.name}"?`)) return;
    try {
      await updateSupplier.mutateAsync({ id: s.id, isActive: !s.isActive });
      toast.success(`Proveedor ${s.isActive ? "desactivado" : "reactivado"}.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  if (traceFor) return <SupplierBatchesModal supplierId={traceFor.id} supplierName={traceFor.name} onClose={() => setTraceFor(null)} />;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Proveedores</h2>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-gray-500">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Mostrar inactivos
            </label>
            <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {suppliers?.map((s) =>
            editingId === s.id ? (
              <div key={s.id} className="space-y-2 rounded-md border border-brand-200 bg-brand-50/40 p-2">
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="Nombre" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} />
                  <input className="input-field" placeholder="NIT/Cédula" value={editDraft.taxId} onChange={(e) => setEditDraft({ ...editDraft, taxId: e.target.value })} />
                  <input className="input-field" placeholder="Teléfono" value={editDraft.phone} onChange={(e) => setEditDraft({ ...editDraft, phone: e.target.value })} />
                  <input className="input-field" placeholder="Correo" value={editDraft.email} onChange={(e) => setEditDraft({ ...editDraft, email: e.target.value })} />
                </div>
                <input className="input-field w-full" placeholder="Dirección" value={editDraft.address} onChange={(e) => setEditDraft({ ...editDraft, address: e.target.value })} />
                <div className="flex justify-end gap-2">
                  <button type="button" className="btn-secondary" onClick={() => setEditingId(null)}>
                    Cancelar
                  </button>
                  <button type="button" className="btn-primary" disabled={updateSupplier.isPending} onClick={saveEdit}>
                    Guardar
                  </button>
                </div>
              </div>
            ) : (
              <div key={s.id} className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm hover:bg-gray-50 ${!s.isActive ? "opacity-50" : ""}`}>
                <div>
                  <span className="text-gray-700">{s.name}</span>
                  {!s.isActive && <span className="ml-2 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">Inactivo</span>}
                  <p className="text-xs text-gray-400">{[s.taxId, s.phone, s.email].filter(Boolean).join(" · ")}</p>
                </div>
                <div className="flex items-center gap-3 text-xs font-medium">
                  <button type="button" className="text-gray-400 hover:text-brand-600" onClick={() => setTraceFor({ id: s.id, name: s.name })}>
                    Lotes
                  </button>
                  <button type="button" className="text-gray-400 hover:text-brand-600" onClick={() => startEdit(s)}>
                    Editar
                  </button>
                  <button type="button" className={s.isActive ? "text-gray-400 hover:text-red-600" : "text-gray-400 hover:text-green-600"} onClick={() => toggleActive(s)}>
                    {s.isActive ? "Desactivar" : "Reactivar"}
                  </button>
                </div>
              </div>
            )
          )}
          {suppliers?.length === 0 && <p className="text-sm text-gray-400">Ningún proveedor todavía.</p>}
        </div>
        <div className="grid grid-cols-3 gap-2 border-t border-gray-100 p-4">
          <input className="input-field col-span-1" placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input-field col-span-1" placeholder="NIT/Cédula" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
          <input className="input-field col-span-1" placeholder="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <button type="button" className="btn-primary col-span-3" disabled={createSupplier.isPending} onClick={handleCreate}>
            {createSupplier.isPending ? "Agregando..." : "Agregar proveedor"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Trazabilidad: todos los lotes (de cualquier producto) que entregó este proveedor — para responder rápido ante una alerta sanitaria. */
function SupplierBatchesModal({ supplierId, supplierName, onClose }: { supplierId: string; supplierName: string; onClose: () => void }) {
  const { data: batches, isLoading } = useSupplierBatches(supplierId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[80vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Lotes entregados — {supplierName}</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto p-4">
          {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
          {batches?.map((b) => (
            <div key={b.id} className="rounded-md px-2 py-2 text-sm hover:bg-gray-50">
              <p className="text-gray-700">
                {b.product?.name} <span className="text-gray-400">({b.product?.sku})</span>
              </p>
              <p className="text-xs text-gray-500">
                {b.batchNumber ?? "Sin número"} · {b.quantity} {b.product ? PRODUCT_UNIT_LABEL[b.product.unit] : ""}
                {b.supplierLotRef ? ` · ref. proveedor: ${b.supplierLotRef}` : ""}
              </p>
              <p className="text-xs text-gray-400">Recibido: {b.receivedAt} {b.expirationDate ? `· Vence: ${b.expirationDate}` : ""}</p>
            </div>
          ))}
          {batches?.length === 0 && <p className="text-sm text-gray-400">Este proveedor no tiene lotes registrados todavía.</p>}
        </div>
      </div>
    </div>
  );
}
